import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { financeApi, type TrialBalanceReport, type TrialBalanceRow, type AccountType } from '@/api/financeApi';
import { formatINR } from '@/utils/format';

const typeOrder: AccountType[] = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

const typeLabels: Record<string, string> = {
  ASSET: 'Assets',
  LIABILITY: 'Liabilities',
  EQUITY: 'Equity & Reserves',
  INCOME: 'Income',
  EXPENSE: 'Expenses',
};

const typeColors: Record<string, string> = {
  ASSET: 'bg-blue-50 text-blue-700 border-blue-200',
  LIABILITY: 'bg-red-50 text-red-700 border-red-200',
  EQUITY: 'bg-purple-50 text-purple-700 border-purple-200',
  INCOME: 'bg-green-50 text-green-700 border-green-200',
  EXPENSE: 'bg-amber-50 text-amber-700 border-amber-200',
};

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const csv = [
    headers.join(','),
    ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function TrialBalancePage() {
  const navigate = useNavigate();
  const toast = useToast();

  const today = new Date().toISOString().split('T')[0];
  const currentYear = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1;

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<TrialBalanceReport | null>(null);
  const [fromDate, setFromDate] = useState(`${currentYear}-04-01`);
  const [asOfDate, setAsOfDate] = useState(today);
  const [typeFilter, setTypeFilter] = useState<AccountType | 'ALL'>('ALL');
  const [showZeroBalance, setShowZeroBalance] = useState(false);
  const [search, setSearch] = useState('');

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await financeApi.getTrialBalance({ asOfDate, fromDate });
      setReport(data);
    } catch (err: any) {
      console.error('Failed to load Trial Balance:', err);
      toast.error(err.response?.data?.message || 'Failed to load Trial Balance');
    } finally {
      setLoading(false);
    }
  }, [asOfDate, fromDate, toast]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const filteredRows = useMemo(() => {
    if (!report) return [];
    let list = report.rows;
    if (typeFilter !== 'ALL') {
      list = list.filter((r) => r.type === typeFilter);
    }
    if (!showZeroBalance) {
      list = list.filter((r) => Math.abs(r.closingDebit) > 0.01 || Math.abs(r.closingCredit) > 0.01 || r.periodDebit > 0 || r.periodCredit > 0);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q) || r.groupName.toLowerCase().includes(q));
    }
    return list;
  }, [report, typeFilter, showZeroBalance, search]);

  // Group by category
  const groupedByType = useMemo(() => {
    const map = new Map<AccountType, TrialBalanceRow[]>();
    typeOrder.forEach((t) => map.set(t, []));
    filteredRows.forEach((r) => {
      const arr = map.get(r.type);
      if (arr) arr.push(r);
    });
    return map;
  }, [filteredRows]);

  const totals = useMemo(() => {
    let deb = 0;
    let cred = 0;
    filteredRows.forEach((r) => {
      deb += r.closingDebit;
      cred += r.closingCredit;
    });
    return {
      debit: Number(deb.toFixed(2)),
      credit: Number(cred.toFixed(2)),
      diff: Number(Math.abs(deb - cred).toFixed(2)),
      isBalanced: Math.abs(deb - cred) < 1,
    };
  }, [filteredRows]);

  const handleExport = () => {
    if (!report) return;
    const headers = ['Account Code', 'Account Name', 'Group', 'Type', 'Opening Balance', 'Period Debit', 'Period Credit', 'Closing Debit', 'Closing Credit'];
    const dataRows = filteredRows.map((r) => [
      r.code,
      r.name,
      r.groupName,
      r.type,
      r.openingBalance,
      r.periodDebit,
      r.periodCredit,
      r.closingDebit,
      r.closingCredit,
    ]);
    dataRows.push(['TOTAL', '', '', '', '', '', '', totals.debit, totals.credit]);
    downloadCSV(`Trial_Balance_${asOfDate}.csv`, headers, dataRows);
    toast.success('Trial Balance exported as CSV');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <AppLayout>
      <div className="flex flex-col h-full bg-[#f8fafc]">
        <div className="flex-1 overflow-y-auto pb-16">
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 print:hidden">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">Trial Balance</h1>
                {report && (
                  <span
                    className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${
                      totals.isBalanced
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                        : 'bg-rose-50 text-rose-800 border-rose-300'
                    }`}
                  >
                    {totals.isBalanced ? 'Balanced ✅' : `Imbalance: ${formatINR(totals.diff)} ⚠️`}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                Summary of all general ledger accounts verifying that total debits equal total credits.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-printer-line text-sm text-slate-500" /> Print
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-download-2-line text-sm text-primary-600" /> Export CSV
              </button>
              <button
                onClick={() => navigate('/finance/journal/new')}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow-sm cursor-pointer"
              >
                <i className="ri-add-line text-sm" /> New Journal Entry
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="px-6 pt-5 flex items-center justify-between gap-4 flex-wrap print:hidden">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 text-sm text-slate-600 bg-white border border-[#e2e8f0] rounded-lg px-2.5 h-9">
                <span className="text-slate-400">From:</span>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="text-sm text-[#1e293b] focus:outline-none bg-transparent"
                />
                <span className="text-slate-400">As Of:</span>
                <input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  className="text-sm text-[#1e293b] focus:outline-none bg-transparent"
                />
              </div>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as AccountType | 'ALL')}
                className="h-9 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] cursor-pointer"
              >
                <option value="ALL">All Account Types</option>
                <option value="ASSET">Assets</option>
                <option value="LIABILITY">Liabilities</option>
                <option value="EQUITY">Equity</option>
                <option value="INCOME">Income</option>
                <option value="EXPENSE">Expenses</option>
              </select>

              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter accounts..."
                  className="w-56 h-9 pl-8 pr-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] text-[#1e293b] placeholder:text-slate-400"
                />
              </div>

              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showZeroBalance}
                  onChange={(e) => setShowZeroBalance(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-[#e2e8f0] text-primary-600 focus:ring-0"
                />
                Show Zero Balance
              </label>
            </div>

            <div className="text-xs text-slate-500 font-medium">
              Showing {filteredRows.length} accounts
            </div>
          </div>

          {/* Trial Balance Table */}
          <div className="px-6 pt-5">
            <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
              {loading ? (
                <div className="py-20 text-center text-slate-400 flex flex-col items-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
                  <p className="text-xs">Generating Trial Balance...</p>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b border-slate-100 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="text-left px-5 py-3 w-28">Account Code</th>
                      <th className="text-left px-4 py-3">Account Name</th>
                      <th className="text-left px-4 py-3 w-36">Ledger Group</th>
                      <th className="text-right px-4 py-3 w-28">Opening Bal</th>
                      <th className="text-right px-4 py-3 w-28">Debit Movement</th>
                      <th className="text-right px-4 py-3 w-28">Credit Movement</th>
                      <th className="text-right px-4 py-3 w-32 bg-blue-50/40 text-blue-900 font-bold">Closing Debit</th>
                      <th className="text-right px-5 py-3 w-32 bg-purple-50/40 text-purple-900 font-bold">Closing Credit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {typeOrder.map((typeKey) => {
                      const typeRows = groupedByType.get(typeKey) || [];
                      if (typeRows.length === 0) return null;

                      const typeDebTotal = typeRows.reduce((s, r) => s + r.closingDebit, 0);
                      const typeCredTotal = typeRows.reduce((s, r) => s + r.closingCredit, 0);

                      return (
                        <div key={typeKey} style={{ display: 'contents' }}>
                          {/* Type Category Header Row */}
                          <tr className="bg-slate-100/60 font-bold text-[#1e293b]">
                            <td colSpan={6} className="px-5 py-2.5 flex items-center gap-2">
                              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${typeColors[typeKey]}`}>
                                {typeLabels[typeKey]}
                              </span>
                              <span className="text-slate-500 font-normal text-[11px]">({typeRows.length} accounts)</span>
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono font-bold text-blue-900 bg-blue-50/40">
                              {formatINR(typeDebTotal)}
                            </td>
                            <td className="px-5 py-2.5 text-right font-mono font-bold text-purple-900 bg-purple-50/40">
                              {formatINR(typeCredTotal)}
                            </td>
                          </tr>

                          {/* Account Rows */}
                          {typeRows.map((r) => (
                            <tr
                              key={r.id}
                              onClick={() => navigate(`/finance/ledger?accountId=${r.id}`)}
                              className="hover:bg-primary-50/30 cursor-pointer transition-colors"
                              title="Click to view Account Ledger statement"
                            >
                              <td className="px-5 py-2.5 font-mono text-slate-700 pl-8">{r.code}</td>
                              <td className="px-4 py-2.5">
                                <span className="font-semibold text-[#1e293b] hover:text-primary-600 transition-colors">
                                  {r.name}
                                </span>
                              </td>
                              <td className="px-4 py-2.5 text-slate-600">{r.groupName}</td>
                              <td className="px-4 py-2.5 text-right font-mono text-slate-500">
                                {formatINR(r.openingBalance)}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-slate-700">
                                {r.periodDebit > 0 ? formatINR(r.periodDebit) : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono text-slate-700">
                                {r.periodCredit > 0 ? formatINR(r.periodCredit) : '—'}
                              </td>
                              <td className="px-4 py-2.5 text-right font-mono font-bold text-[#1e293b] bg-blue-50/20">
                                {r.closingDebit > 0 ? formatINR(r.closingDebit) : '—'}
                              </td>
                              <td className="px-5 py-2.5 text-right font-mono font-bold text-[#1e293b] bg-purple-50/20">
                                {r.closingCredit > 0 ? formatINR(r.closingCredit) : '—'}
                              </td>
                            </tr>
                          ))}
                        </div>
                      );
                    })}

                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-slate-400">
                          No matching trial balance rows.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-slate-100/90 font-bold border-t-2 border-slate-300">
                    <tr>
                      <td colSpan={6} className="px-5 py-3 text-right uppercase tracking-wider text-xs text-slate-800 font-bold">
                        GRAND TOTAL (Debits vs Credits)
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-sm font-bold text-blue-950 bg-blue-100/50">
                        {formatINR(totals.debit)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-sm font-bold text-purple-950 bg-purple-100/50">
                        {formatINR(totals.credit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}